import { Resource } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';

export const conventionsResourceTemplate: Resource = {
  uri: 'smartstack://conventions',
  name: 'AtlasHub Conventions',
  description: 'Documentation of AtlasHub/SmartStack naming conventions, patterns, and best practices',
  mimeType: 'text/markdown',
};

export async function getConventionsResource(config: Config): Promise<string> {
  const { schemas, tablePrefixes, scopeTypes, migrationFormat, namespaces, servicePattern } = config.conventions;

  return `# AtlasHub SmartStack Conventions

## Overview

This document describes the mandatory conventions for extending the SmartStack/AtlasHub platform.
Following these conventions ensures compatibility and prevents conflicts.

---

## 1. Database Conventions

### SQL Schemas

SmartStack uses SQL Server schemas to separate platform tables from client extensions:

| Schema | Usage | Description |
|--------|-------|-------------|
| \`${schemas.platform}\` | SmartStack platform | All native SmartStack tables |
| \`${schemas.extensions}\` | Client extensions | Custom tables added by clients |

### Domain Table Prefixes

Tables are organized by domain using prefixes:

| Prefix | Domain | Example Tables |
|--------|--------|----------------|
| \`auth_\` | Authorization | auth_Users, auth_Roles, auth_Permissions |
| \`nav_\` | Navigation | nav_Contexts, nav_Applications, nav_Modules |
| \`usr_\` | User profiles | usr_Profiles, usr_Preferences |
| \`ai_\` | AI features | ai_Providers, ai_Models, ai_Prompts |
| \`cfg_\` | Configuration | cfg_Settings |
| \`wkf_\` | Workflows | wkf_EmailTemplates, wkf_Workflows |
| \`support_\` | Support | support_Tickets, support_Comments |
| \`entra_\` | Entra sync | entra_Groups, entra_SyncState |
| \`ref_\` | References | ref_Companies, ref_Departments |
| \`loc_\` | Localization | loc_Languages, loc_Translations |
| \`lic_\` | Licensing | lic_Licenses |
| \`tenant_\` | Multi-Tenancy | tenant_Tenants, tenant_TenantUsers, tenant_TenantUserRoles |

### Navigation Scope System

All navigation data (Context, Application, Module, Section) uses a **Scope** field to distinguish system data from client extensions:

| Scope | Usage | Description | Example Code |
|-------|-------|-------------|--------------|
| \`${scopeTypes[0]}\` | SmartStack platform | Protected, delivered with SmartStack | \`administration\`, \`users\` |
| \`${scopeTypes[1]}\` | Client extensions | Custom, added by clients | \`inventory\`, \`custom_app\` |
| \`${scopeTypes[2]}\` | Partner modules | Provided by certified partners | \`partner_integration\` |
| \`${scopeTypes[3]}\` | Community modules | Open-source community contributions | \`community_tool\` |

**Navigation Hierarchy (4 levels):**

\`\`\`
Context → Application → Module → Section
\`\`\`

**Examples for each level:**

| Level | Core Example | Extension Example |
|-------|--------------|-------------------|
| Context | Code: \`platform\`<br/>Scope: \`${scopeTypes[0]}\` | Code: \`business\`<br/>Scope: \`${scopeTypes[1]}\` |
| Application | Code: \`administration\`<br/>Scope: \`${scopeTypes[0]}\` | Code: \`custom_app\`<br/>Scope: \`${scopeTypes[1]}\` |
| Module | Code: \`users\`<br/>Route: \`platform.administration.users\`<br/>Scope: \`${scopeTypes[0]}\` | Code: \`inventory\`<br/>Route: \`business.operations.inventory\`<br/>Scope: \`${scopeTypes[1]}\` |
| Section | Code: \`management\`<br/>Scope: \`${scopeTypes[0]}\` | Code: \`reports\`<br/>Scope: \`${scopeTypes[1]}\` |

**Rules:**
1. \`${scopeTypes[0]}\` modules are **protected** - clients cannot create or modify them
2. \`${scopeTypes[1]}\` modules are **free** - clients can create custom navigation
3. SmartStack updates will never overwrite \`${scopeTypes[1]}\` data
4. **Routes must be globally unique** (enforced by unique constraint on Route column)
5. Codes no longer use prefixes - the Scope field provides categorization

### RBAC Scope System

Permissions and Roles also use the **Scope** field for the same categorization:

#### Permissions

| Table | Field | Values | Protection |
|-------|-------|--------|------------|
| \`core.auth_Permissions\` | \`Scope\` | ${scopeTypes.join(', ')} | ${scopeTypes[0]} permissions cannot be deleted |

**Automatic Scope Detection:**

Permissions inherit their Scope from the module's NavRoute:

\`\`\`
NavRoute: "platform.administration.users"
Module Scope: Core
→ Permissions Scope: Core
  - platform.administration.users.read (Core)
  - platform.administration.users.create (Core)
  - platform.administration.users.update (Core)
  - platform.administration.users.delete (Core)
\`\`\`

**Detection Rules:**
- Routes starting with \`platform.*\` or \`business.*\` (system contexts) → \`${scopeTypes[0]}\`
- Routes from Extension contexts → \`${scopeTypes[1]}\`
- Partner-provided routes → \`${scopeTypes[2]}\`
- Community routes → \`${scopeTypes[3]}\`

#### Roles

| Table | Field | Values | Protection |
|-------|-------|--------|------------|
| \`core.auth_Roles\` | \`Scope\` | ${scopeTypes.join(', ')} | ${scopeTypes[0]} roles cannot be deleted |

**Default Roles (Microsoft Fabric Model):**

All default roles have \`Scope = '${scopeTypes[0]}'\` (protected):

| Code | Scope | Permissions |
|------|-------|-------------|
| \`admin\` | ${scopeTypes[0]} | All permissions (read, create, update, delete) |
| \`member\` | ${scopeTypes[0]} | Read, create, update + user management (no delete) |
| \`contributor\` | ${scopeTypes[0]} | Read, create, update (no delete, no user management) |
| \`viewer\` | ${scopeTypes[0]} | Read only |

**Custom Roles:**

Clients can create roles with \`Scope = '${scopeTypes[1]}'\`:

\`\`\`sql
INSERT INTO core.auth_Roles (Id, Code, Scope, Name, Description, ...)
VALUES (
    NEWID(),
    'sales_manager',
    '${scopeTypes[1]}',  -- Custom role
    'Sales Manager',
    'Manage sales operations',
    ...
);
\`\`\`

**Protection Rules:**
- \`${scopeTypes[0]}\` roles and permissions: **Read-only** for clients (seeded with migrations)
- \`${scopeTypes[1]}\` roles and permissions: **Full control** for clients
- \`${scopeTypes[2]}\` roles and permissions: **Managed by partners**
- \`${scopeTypes[3]}\` roles and permissions: **Open-source community**

### Entity Configuration

\`\`\`csharp
public class MyEntityConfiguration : IEntityTypeConfiguration<MyEntity>
{
    public void Configure(EntityTypeBuilder<MyEntity> builder)
    {
        // CORRECT: Use schema + domain prefix
        builder.ToTable("auth_Users", "${schemas.platform}");

        // WRONG: No schema specified
        // builder.ToTable("auth_Users");

        // WRONG: No domain prefix
        // builder.ToTable("Users", "${schemas.platform}");
    }
}
\`\`\`

### Extending Core Entities

When extending a core entity, use the \`${schemas.extensions}\` schema:

\`\`\`csharp
// Client extension for auth_Users
public class UserExtension
{
    public Guid UserId { get; set; }  // FK to auth_Users
    public User User { get; set; }

    // Custom properties
    public string CustomField { get; set; }
}

// Configuration - use extensions schema
builder.ToTable("client_UserExtensions", "${schemas.extensions}");
builder.HasOne(e => e.User)
       .WithOne()
       .HasForeignKey<UserExtension>(e => e.UserId);
\`\`\`

---

## 2. Migration Conventions

### Naming Format

Migrations MUST follow this naming pattern:

\`\`\`
${migrationFormat}
\`\`\`

| Part | Description | Example |
|------|-------------|---------|
| \`{context}\` | DbContext name | \`core\`, \`extensions\` |
| \`{version}\` | Semver version | \`v1.0.0\`, \`v1.2.0\` |
| \`{sequence}\` | Order in version | \`001\`, \`002\` |
| \`{Description}\` | Action (PascalCase) | \`CreateAuthUsers\` |

**Examples:**
- \`core_v1.0.0_001_InitialSchema.cs\`
- \`core_v1.0.0_002_CreateAuthUsers.cs\`
- \`core_v1.2.0_001_AddUserProfiles.cs\`
- \`extensions_v1.0.0_001_AddClientFeatures.cs\`

### Creating Migrations

\`\`\`bash
# Create a new migration
dotnet ef migrations add core_v1.0.0_001_InitialSchema

# With context specified
dotnet ef migrations add core_v1.2.0_001_AddUserProfiles --context ApplicationDbContext
\`\`\`

### Migration Rules

1. **One migration per feature** - Group related changes in a single migration
2. **Version-based naming** - Use semver (v1.0.0, v1.2.0) to link migrations to releases
3. **Sequence numbers** - Use NNN (001, 002, etc.) for migrations in the same version
4. **Context prefix** - Use \`core_\` for platform tables, \`extensions_\` for client extensions
5. **Descriptive names** - Use clear PascalCase descriptions (CreateAuthUsers, AddUserProfiles, etc.)
6. **Schema must be specified** - All tables must specify their schema in ToTable()

---

## 3. Namespace Conventions

### Layer Structure

| Layer | Namespace | Purpose |
|-------|-----------|---------|
| Domain | \`${namespaces.domain}\` | Entities, value objects, domain events |
| Application | \`${namespaces.application}\` | Use cases, services, interfaces |
| Infrastructure | \`${namespaces.infrastructure}\` | EF Core, external services |
| API | \`${namespaces.api}\` | Controllers, DTOs, middleware |

### Example Namespaces

\`\`\`csharp
// Entity in Domain layer
namespace ${namespaces.domain}.Entities;
public class User { }

// Service interface in Application layer
namespace ${namespaces.application}.Services;
public interface IUserService { }

// EF Configuration in Infrastructure layer
namespace ${namespaces.infrastructure}.Persistence.Configurations;
public class UserConfiguration { }

// Controller in API layer
namespace ${namespaces.api}.Controllers;
public class UsersController { }
\`\`\`

---

## 4. Service Conventions

### Naming Pattern

| Type | Pattern | Example |
|------|---------|---------|
| Interface | \`${servicePattern.interface.replace('{Name}', '<Name>')}\` | \`IUserService\` |
| Implementation | \`${servicePattern.implementation.replace('{Name}', '<Name>')}\` | \`UserService\` |

### Service Structure

\`\`\`csharp
// Interface (in Application layer)
namespace ${namespaces.application}.Services;

public interface IUserService
{
    Task<UserDto> GetByIdAsync(Guid id, CancellationToken ct = default);
    Task<IEnumerable<UserDto>> GetAllAsync(CancellationToken ct = default);
    Task<UserDto> CreateAsync(CreateUserCommand cmd, CancellationToken ct = default);
    Task UpdateAsync(Guid id, UpdateUserCommand cmd, CancellationToken ct = default);
    Task DeleteAsync(Guid id, CancellationToken ct = default);
}

// Implementation (in Infrastructure or Application layer)
public class UserService : IUserService
{
    private readonly IRepository<User> _repository;
    private readonly ILogger<UserService> _logger;

    public UserService(IRepository<User> repository, ILogger<UserService> logger)
    {
        _repository = repository;
        _logger = logger;
    }

    // Implementation...
}
\`\`\`

### Dependency Injection

\`\`\`csharp
// In DependencyInjection.cs or ServiceCollectionExtensions.cs
public static IServiceCollection AddApplicationServices(this IServiceCollection services)
{
    services.AddScoped<IUserService, UserService>();
    services.AddScoped<IRoleService, RoleService>();
    // ...
    return services;
}
\`\`\`

---

## 5. Extension Patterns

### Extending Services

Use DI replacement to override core services:

\`\`\`csharp
// Your custom implementation
public class CustomUserService : UserService, IUserService
{
    public CustomUserService(IRepository<User> repo, ILogger<CustomUserService> logger)
        : base(repo, logger) { }

    public override async Task<UserDto> GetByIdAsync(Guid id, CancellationToken ct = default)
    {
        // Custom logic before
        var result = await base.GetByIdAsync(id, ct);
        // Custom logic after
        return result;
    }
}

// Replace in DI
services.Replace(ServiceDescriptor.Scoped<IUserService, CustomUserService>());
\`\`\`

### Extension Hooks

Core services expose hooks for common extension points:

\`\`\`csharp
public interface IUserServiceHooks
{
    Task OnUserCreatedAsync(User user, CancellationToken ct);
    Task OnUserUpdatedAsync(User user, CancellationToken ct);
    Task OnUserDeletedAsync(Guid userId, CancellationToken ct);
}
\`\`\`

---

## 6. Configuration

### appsettings.json Structure

\`\`\`json
{
  "AtlasHub": {
    "Licensing": {
      "Key": "XXXX-XXXX-XXXX-XXXX",
      "Validate": true
    },
    "Features": {
      "AI": true,
      "Support": true,
      "Workflows": true
    }
  },
  "Client": {
    "Custom": {
      // Client-specific configuration
    }
  }
}
\`\`\`

---

## 7. Entity Conventions

### Required Interfaces

All entities MUST implement the following interfaces:

| Interface | Properties | Required |
|-----------|------------|----------|
| \`ITenantEntity\` | \`TenantId\` (Guid) | **YES** (except system entities) |
| \`IHasCode\` | \`Code\` (string, lowercase, max 100) | **YES** |
| \`ISoftDeletable\` | \`IsDeleted\`, \`DeletedAt\`, \`DeletedBy\` | **YES** |
| \`IVersioned\` | \`RowVersion\` (byte[]) | **YES** |
| \`IHistoryEntity<T>\` | \`SourceId\`, \`ChangeType\`, \`OldValues\`, \`NewValues\`, \`ChangedAt\`, \`ChangedBy\` | For history tables |

### Required Fields (All Entities)

| Field | Type | Description |
|-------|------|-------------|
| \`Id\` | \`Guid\` | Primary key |
| \`TenantId\` | \`Guid\` | **Tenant identifier** (required for multi-tenant isolation) |
| \`Code\` | \`string\` | Unique identifier per tenant (lowercase, max 100) |
| \`CreatedAt\` | \`DateTime\` | Creation timestamp |
| \`UpdatedAt\` | \`DateTime?\` | Last update timestamp |
| \`CreatedBy\` | \`string?\` | User who created the entity |
| \`UpdatedBy\` | \`string?\` | User who last updated the entity |
| \`IsDeleted\` | \`bool\` | Soft delete flag |
| \`DeletedAt\` | \`DateTime?\` | Deletion timestamp |
| \`DeletedBy\` | \`string?\` | User who deleted the entity |
| \`RowVersion\` | \`byte[]\` | Concurrency token |

### Multi-Tenant Strategy

\`\`\`csharp
// Interface for tenant-scoped entities
public interface ITenantEntity
{
    Guid TenantId { get; set; }
}

// Global query filter (applied automatically)
builder.HasQueryFilter(e => e.TenantId == _currentTenantService.TenantId);
\`\`\`

**System Entities (without TenantId):**
- Licenses (\`lic_\`)
- Global configuration (\`cfg_\` system level)
- Shared reference tables

Use \`ISystemEntity\` marker interface for these entities.

### Code Field Rules

1. **Always lowercase** - Normalized via setter and value converter
2. **No prefixes required** - Use Scope field for categorization (navigation entities)
3. **Unique composite index**: \`(TenantId, Code) WHERE IsDeleted = 0\`
4. **Max length**: 100 characters
5. **Navigation routes**: Globally unique via unique constraint on Route column

\`\`\`csharp
// Code property with automatic lowercase normalization
private string _code = string.Empty;
public string Code
{
    get => _code;
    set => _code = value?.ToLowerInvariant() ?? string.Empty;
}
\`\`\`

### Soft Delete with Restore

\`\`\`csharp
public interface ISoftDeletable
{
    bool IsDeleted { get; set; }
    DateTime? DeletedAt { get; set; }
    string? DeletedBy { get; set; }
}

// Domain methods
public void SoftDelete(string? deletedBy = null)
{
    IsDeleted = true;
    DeletedAt = DateTime.UtcNow;
    DeletedBy = deletedBy;
}

public void Restore(string? restoredBy = null)
{
    IsDeleted = false;
    DeletedAt = null;
    DeletedBy = null;
    UpdatedAt = DateTime.UtcNow;
    UpdatedBy = restoredBy;
}
\`\`\`

### History Tables (JSON Pattern)

\`\`\`csharp
public interface IHistoryEntity<TSourceId>
{
    Guid Id { get; set; }
    TSourceId SourceId { get; set; }
    ChangeType ChangeType { get; set; }  // Created, Updated, Deleted
    DateTime ChangedAt { get; set; }
    string? ChangedBy { get; set; }
    string? OldValues { get; set; }  // JSON snapshot before change
    string? NewValues { get; set; }  // JSON snapshot after change
}

public enum ChangeType { Created = 1, Updated = 2, Deleted = 3 }
\`\`\`

### Complete Entity Example

\`\`\`csharp
public class MyEntity : BaseEntity
{
    // === REQUIRED (from BaseEntity) ===
    // public Guid Id { get; set; }
    // public Guid TenantId { get; set; }
    // public string Code { get; set; }  // lowercase, unique per tenant
    // public DateTime CreatedAt { get; set; }
    // public DateTime? UpdatedAt { get; set; }
    // public string? CreatedBy { get; set; }
    // public string? UpdatedBy { get; set; }
    // public bool IsDeleted { get; set; }
    // public DateTime? DeletedAt { get; set; }
    // public string? DeletedBy { get; set; }
    // public byte[] RowVersion { get; set; }

    // === BUSINESS PROPERTIES ===
    public string Name { get; private set; } = null!;

    private MyEntity() { }

    public static MyEntity Create(Guid tenantId, string code, string name, string? createdBy = null)
    {
        return new MyEntity
        {
            Id = Guid.NewGuid(),
            TenantId = tenantId,
            Code = code.ToLowerInvariant(),
            Name = name,
            CreatedAt = DateTime.UtcNow,
            CreatedBy = createdBy
        };
    }
}
\`\`\`

### EF Core Configuration

\`\`\`csharp
public class MyEntityConfiguration : IEntityTypeConfiguration<MyEntity>
{
    public void Configure(EntityTypeBuilder<MyEntity> builder)
    {
        builder.ToTable("domain_MyEntities", "${schemas.platform}");
        builder.HasKey(e => e.Id);

        // Multi-tenant
        builder.Property(e => e.TenantId).IsRequired();
        builder.HasIndex(e => e.TenantId);

        // Code: lowercase, unique per tenant (filtered)
        builder.Property(e => e.Code).HasMaxLength(100).IsRequired();
        builder.HasIndex(e => new { e.TenantId, e.Code })
            .IsUnique()
            .HasFilter("[IsDeleted] = 0");

        // Concurrency
        builder.Property(e => e.RowVersion).IsRowVersion();

        // Audit fields
        builder.Property(e => e.CreatedBy).HasMaxLength(256);
        builder.Property(e => e.UpdatedBy).HasMaxLength(256);
        builder.Property(e => e.DeletedBy).HasMaxLength(256);
    }
}
\`\`\`

### Entity Types Summary

| Type | Fields | Use Case |
|------|--------|----------|
| **BaseEntity** | All required fields | Standard tenant-scoped entities |
| **SystemEntity** | All except TenantId | Shared system entities |
| **HistoryEntity** | Id, TenantId, SourceId, ChangeType, Values, ChangedAt/By | Audit trail tables |

---

## 8. Multi-Tenant Architecture

### Overview

SmartStack implements a comprehensive multi-tenant architecture where:
- **Platform scope**: Users with roles via \`UserRole\` (no tenant) see ALL tenants
- **Tenant scope**: Users with roles via \`TenantUserRole\` see ONLY their tenant

### Tenant Model

\`\`\`
License (1) ──────► (N) Tenant
                         │
                         ├──► (1:1) Organisation (if B2B)
                         │
                         └──► (N) TenantUser ──► (N) TenantUserRole
                                    │
                                    └──► User (global)
\`\`\`

### Core Entities

#### Tenant

| Field | Type | Description |
|-------|------|-------------|
| \`Id\` | Guid | Primary key |
| \`LicenseId\` | Guid (FK) | Link to License (features & limits) |
| \`Slug\` | string? | Optional vanity URL (unique, lowercase, alphanum + hyphen) |
| \`Name\` | string | Display name |
| \`Type\` | enum | B2B (with Organisation) or B2C |
| \`Status\` | enum | Active, Suspended, Deleted |
| \`CreatedBy\` | Guid (FK) | User who created the tenant |
| \`CreatedAt\` | DateTime | Creation timestamp |

\`\`\`csharp
public class Tenant : SystemEntity
{
    public Guid LicenseId { get; private set; }
    public License License { get; private set; }

    public string? Slug { get; private set; }  // Vanity URL (optional)
    public string Name { get; private set; }
    public TenantType Type { get; private set; }
    public TenantStatus Status { get; private set; }

    public Guid CreatedBy { get; private set; }
    public User Creator { get; private set; }

    // Navigation
    public ICollection<TenantUser> TenantUsers { get; private set; }
    public Organisation? Organisation { get; private set; }  // If B2B
}

public enum TenantType { B2B = 1, B2C = 2 }
public enum TenantStatus { Active = 1, Suspended = 2, Deleted = 3 }
\`\`\`

#### TenantUser

Links a User to a Tenant with membership metadata.

| Field | Type | Description |
|-------|------|-------------|
| \`Id\` | Guid | Primary key |
| \`TenantId\` | Guid (FK) | **NOT NULL** - Tenant reference |
| \`UserId\` | Guid (FK) | User reference |
| \`IsDefault\` | bool | Default tenant for this user (unique per user) |
| \`Status\` | enum | Pending, Active, Revoked |
| \`JoinedAt\` | DateTime | When user joined tenant |

\`\`\`csharp
public class TenantUser
{
    public Guid Id { get; private set; }
    public Guid TenantId { get; private set; }  // NOT NULL
    public Tenant Tenant { get; private set; }

    public Guid UserId { get; private set; }
    public User User { get; private set; }

    public bool IsDefault { get; private set; }
    public TenantUserStatus Status { get; private set; }
    public DateTime JoinedAt { get; private set; }

    // Roles within this tenant
    public ICollection<TenantUserRole> Roles { get; private set; }
}

public enum TenantUserStatus { Pending = 1, Active = 2, Revoked = 3 }
\`\`\`

#### TenantUserRole

Assigns roles to a user within a specific tenant.

| Field | Type | Description |
|-------|------|-------------|
| \`TenantUserId\` | Guid (FK) | TenantUser reference |
| \`RoleId\` | Guid (FK) | Role reference |
| \`AssignedAt\` | DateTime | When role was assigned |
| \`AssignedBy\` | string? | Who assigned the role |

\`\`\`csharp
public class TenantUserRole
{
    public Guid TenantUserId { get; private set; }
    public TenantUser TenantUser { get; private set; }

    public Guid RoleId { get; private set; }
    public Role Role { get; private set; }

    public DateTime AssignedAt { get; private set; }
    public string? AssignedBy { get; private set; }
}
\`\`\`

#### TenantInvitation

Manages user invitations to a tenant.

| Field | Type | Description |
|-------|------|-------------|
| \`Id\` | Guid | Primary key |
| \`TenantId\` | Guid (FK) | Target tenant |
| \`Email\` | string | Invited email address |
| \`RoleId\` | Guid (FK) | Role to assign on acceptance |
| \`Token\` | string | Unique invitation token |
| \`ExpiresAt\` | DateTime | Token expiration |
| \`Status\` | enum | Pending, Accepted, Expired, Revoked |
| \`InvitedBy\` | Guid (FK) | User who sent invitation |
| \`InvitedAt\` | DateTime | Invitation timestamp |

\`\`\`csharp
public class TenantInvitation
{
    public Guid Id { get; private set; }
    public Guid TenantId { get; private set; }
    public Tenant Tenant { get; private set; }

    public string Email { get; private set; }
    public Guid RoleId { get; private set; }
    public Role Role { get; private set; }

    public string Token { get; private set; }
    public DateTime ExpiresAt { get; private set; }
    public TenantInvitationStatus Status { get; private set; }

    public Guid InvitedBy { get; private set; }
    public User Inviter { get; private set; }
    public DateTime InvitedAt { get; private set; }
}

public enum TenantInvitationStatus { Pending = 1, Accepted = 2, Expired = 3, Revoked = 4 }
\`\`\`

### Access Control Matrix

| Role Source | Scope | Description |
|-------------|-------|-------------|
| \`UserRole\` (existing) | Platform | Roles WITHOUT tenant → sees ALL tenants |
| \`TenantUserRole\` (new) | Tenant | Roles WITH tenant → sees ONLY that tenant |

**Examples:**

\`\`\`
UserRole: UserId=1, RoleId=PlatformAdmin    → User 1 sees ALL tenants
UserRole: UserId=2, RoleId=PlatformReader   → User 2 reads ALL tenants

TenantUserRole: TenantUserId=3, RoleId=Admin   → User 3 is Admin of Tenant A only
TenantUserRole: TenantUserId=4, RoleId=User    → User 4 is User of Tenant A only
\`\`\`

### Organisation (B2B Extension)

Organisation is a 1:1 extension of Tenant for B2B scenarios:

\`\`\`csharp
public class Organisation : BaseEntity
{
    public Guid TenantId { get; private set; }  // FK to Tenant
    public Tenant Tenant { get; private set; }

    // Organisation-specific fields
    public string LegalName { get; private set; }
    public string? VatNumber { get; private set; }
    public string? Address { get; private set; }
    // ... other B2B fields
}
\`\`\`

### License Integration

Features and limits are managed via License:

\`\`\`csharp
// License.LimitsJson example
{
    "max_users": 100,
    "max_tenants": 5,
    "max_storage_gb": 50
}

// Check in service
public async Task<bool> CanCreateTenantAsync(Guid licenseId)
{
    var license = await _licenseService.GetAsync(licenseId);
    var limits = JsonSerializer.Deserialize<LicenseLimits>(license.LimitsJson);
    var currentTenants = await _tenantRepo.CountByLicenseAsync(licenseId);
    return currentTenants < limits.MaxTenants;
}
\`\`\`

### URL Routing

Tenant identification in URLs:

| Pattern | Example | Use Case |
|---------|---------|----------|
| GUID (default) | \`/{tenant-guid}/api/...\` | Always works, secure |
| Slug (optional) | \`/{slug}/api/...\` | Vanity URL for premium clients |

\`\`\`csharp
// Middleware resolution priority
1. Extract from URL path (/{tenant-identifier}/...)
2. Check if GUID → resolve directly
3. Check if Slug → resolve by slug
4. Fallback to user's default tenant (IsDefault=true)
\`\`\`

### Audit Trail

Critical tables have history tracking:

- \`TenantUserHistory\` - tracks membership changes
- \`TenantUserRoleHistory\` - tracks role assignment changes

\`\`\`csharp
public class TenantUserHistory : IHistoryEntity<Guid>
{
    public Guid Id { get; set; }
    public Guid SourceId { get; set; }  // TenantUser.Id
    public ChangeType ChangeType { get; set; }
    public string? OldValues { get; set; }  // JSON
    public string? NewValues { get; set; }  // JSON
    public DateTime ChangedAt { get; set; }
    public string? ChangedBy { get; set; }
}
\`\`\`

### Database Constraints

\`\`\`sql
-- Tenant slug unique (if set)
CREATE UNIQUE INDEX IX_Tenant_Slug ON tenant_Tenants(Slug) WHERE Slug IS NOT NULL;

-- Only one default tenant per user
CREATE UNIQUE INDEX IX_TenantUser_IsDefault
ON tenant_TenantUsers(UserId) WHERE IsDefault = 1;

-- Composite key for TenantUserRole
ALTER TABLE tenant_TenantUserRoles
ADD CONSTRAINT PK_TenantUserRole PRIMARY KEY (TenantUserId, RoleId);
\`\`\`

### Table Prefix

All tenant-related tables use the \`tenant_\` prefix:

| Table | Prefix |
|-------|--------|
| Tenants | \`tenant_Tenants\` |
| TenantUsers | \`tenant_TenantUsers\` |
| TenantUserRoles | \`tenant_TenantUserRoles\` |
| TenantInvitations | \`tenant_TenantInvitations\` |
| TenantUserHistory | \`tenant_TenantUserHistory\` |
| TenantUserRoleHistory | \`tenant_TenantUserRoleHistory\` |

---

## 10. Frontend Conventions

### Layout Standards

All context layouts MUST follow the AdminLayout pattern for consistent user experience.

#### Standard Structure

\`\`\`tsx
<main className={\`pt-16 transition-all duration-300 \${
  showSidebar ? (isCollapsed ? 'lg:pl-16' : 'lg:pl-64') : ''
}\`}>
  <div className="p-4 sm:p-6 lg:px-10 h-full overflow-auto">
    <Outlet />
  </div>
</main>
\`\`\`

#### Layout Rules

| Rule | Description |
|------|-------------|
| No \`max-w-*\` | Content MUST occupy full available width |
| Horizontal padding | Use \`lg:px-10\` for consistent padding |
| Scroll container | Use \`h-full overflow-auto\` for internal scrolling |
| Sidebar conditional | \`showSidebar = !!currentAppCode\` |

### Tab Navigation with URL

Pages with tabs MUST synchronize tab state with URL for shareable links and browser navigation.

#### useTabNavigation Hook

\`\`\`tsx
import { useTabNavigation } from '@/hooks/useTabNavigation';

// 1. Define valid tabs
const VALID_TABS = ['info', 'settings', 'permissions'] as const;
type TabId = typeof VALID_TABS[number];

// 2. Use the hook
const { activeTab, setActiveTab } = useTabNavigation<TabId>('info', VALID_TABS);

// 3. Use in JSX
<button onClick={() => setActiveTab('settings')}>Settings</button>
\`\`\`

#### URL Behavior

| URL | Active Tab |
|-----|------------|
| \`/page\` | Default tab (e.g., \`info\`) |
| \`/page?tab=settings\` | \`settings\` |
| \`/page?tab=invalid\` | Default tab (fallback) |

#### Hook Characteristics

- URL is clean for default tab (no \`?tab=\` parameter)
- Invalid tabs fallback to default
- Uses \`replace: true\` to avoid polluting browser history

#### Lazy Loading for Tabs (Performance)

Pages with tabs MUST implement lazy loading to avoid fetching all tab data upfront.

**Pattern 1: useQuery with enabled condition (Recommended)**

\`\`\`tsx
// ❌ BAD - Loads all data on mount
const { data: info } = useQuery(['info', id], fetchInfo);
const { data: settings } = useQuery(['settings', id], fetchSettings);

// ✅ GOOD - Only loads active tab data
const { data: info } = useQuery(
  ['info', id],
  fetchInfo,
  { enabled: activeTab === 'info' }
);

const { data: settings } = useQuery(
  ['settings', id],
  fetchSettings,
  { enabled: activeTab === 'settings' }
);
\`\`\`

**Pattern 2: React.lazy() for heavy tab components**

\`\`\`tsx
// Lazy load tab components
const TabInfo = React.lazy(() => import('./tabs/TabInfo'));
const TabSettings = React.lazy(() => import('./tabs/TabSettings'));

// In JSX with Suspense
<TabsContent value="info">
  <Suspense fallback={<Skeleton />}>
    <TabInfo entityId={id} />
  </Suspense>
</TabsContent>
\`\`\`

**Pattern 3: Conditional rendering**

\`\`\`tsx
// Render tab content only when active
{activeTab === 'settings' && <SettingsTab entityId={id} />}
\`\`\`

| Pattern | Use Case |
|---------|----------|
| useQuery enabled | Simple data fetching per tab |
| React.lazy + Suspense | Heavy components with own data fetching |
| Conditional rendering | Light components, quick tab switches |

### Breadcrumb Navigation

All pages MUST include a Breadcrumb component for consistent navigation and user orientation.

#### Breadcrumb Component

\`\`\`tsx
import { Breadcrumb } from '@/components/ui/Breadcrumb';

// In your page component
<Breadcrumb
  items={[
    { label: t('nav.administration'), href: '/platform/administration' },
    { label: t('nav.users'), href: '/platform/administration/users' },
    { label: user.fullName }  // Last item = current page (no href)
  ]}
/>
\`\`\`

#### Breadcrumb Rules

| Rule | Description |
|------|-------------|
| First item | Context/Application level with Home icon (auto-added if no icon) |
| Intermediate items | Clickable links to parent pages |
| Last item | Current page name, no href, bold text |
| Labels | Use i18n translation keys from \`navigation.json\` |
| Detail pages | Include entity name (e.g., user's full name) |

#### Hierarchy Pattern

\`\`\`
Context > Application > Module > [Section] > [Entity Name]
\`\`\`

**Examples:**

| Page Type | Breadcrumb Items |
|-----------|------------------|
| List page | Administration > Users |
| Detail page | Administration > Users > John Doe |
| Nested page | Administration > Users > John Doe > Permissions |

#### BreadcrumbItem Interface

\`\`\`tsx
interface BreadcrumbItem {
  label: string;      // Display text (use t() for i18n)
  href?: string;      // Link destination (omit for current page)
  icon?: ReactNode;   // Optional icon (Home auto-added to first item)
}
\`\`\`

#### Page Position

Breadcrumb should be the FIRST element in the page content:

\`\`\`tsx
return (
  <div className="space-y-6">
    {/* 1. Breadcrumb - always first */}
    <Breadcrumb items={[...]} />

    {/* 2. Scope Banner (if multi-tenant) */}
    <ScopeBanner />

    {/* 3. Page Header */}
    <div className="flex items-center gap-4">...</div>

    {/* 4. Page Content */}
    ...
  </div>
);
\`\`\`

### Frontend Validation Checks

| Check | Description |
|-------|-------------|
| \`layouts\` | Verify no \`max-w-*\` in content wrapper, standard padding present |
| \`tabs\` | Verify \`useTabNavigation\` hook usage AND lazy loading patterns for pages with tabs |
| \`breadcrumb\` | Verify Breadcrumb component present as first element in page |

---

## Quick Reference

| Category | Convention | Example |
|----------|------------|---------|
| Platform schema | \`${schemas.platform}\` | \`.ToTable("auth_Users", "${schemas.platform}")\` |
| Extensions schema | \`${schemas.extensions}\` | \`.ToTable("client_Custom", "${schemas.extensions}")\` |
| Table prefixes | \`${tablePrefixes.slice(0, 5).join(', ')}\`, etc. | \`auth_Users\`, \`nav_Modules\` |
| Scope types | \`${scopeTypes.join(', ')}\` | Navigation entities use Scope field |
| Navigation codes | No prefixes | \`administration\`, \`users\` (Scope determines origin) |
| Migration | \`{context}_v{version}_{seq}_{Desc}\` | \`core_v1.0.0_001_CreateAuthUsers\` |
| Interface | \`I<Name>Service\` | \`IUserService\` |
| Implementation | \`<Name>Service\` | \`UserService\` |
| Domain namespace | \`${namespaces.domain}\` | - |
| API namespace | \`${namespaces.api}\` | - |
| Required entity fields | Id, TenantId, Code, Audit, SoftDelete, RowVersion | See Entity Conventions |
| Tenant prefix | \`tenant_\` | \`tenant_Tenants\`, \`tenant_TenantUsers\` |
| Platform roles | \`UserRole\` | No TenantId → sees ALL tenants |
| RBAC Scope | Permissions, Roles, Navigation | ${scopeTypes.join(', ')} |
| Default roles | Scope = ${scopeTypes[0]} | admin, member, contributor, viewer |
| Custom roles | Scope = ${scopeTypes[1]} | Client-defined roles |
| Tenant roles | \`TenantUserRole\` | With TenantId → sees ONLY that tenant |
| License:Tenant | 1:N | One License can have multiple Tenants |
| Dry-run scaffold | \`dryRun: true\` | Preview generated code without writing |
| Tenant-aware entity | \`ITenantEntity\` | Default for scaffold_extension |
| System entity | \`isSystemEntity: true\` | No TenantId, platform-level |
| Full-stack scaffold | \`type: "feature"\` | Entity + DTOs + Validators + Repository + Service + Controller + Component |
| Client extension | \`clientExtension: true\` | Auto-sets schema=extensions + prefix=ext_ |
| Unit tests | \`type: "test"\` | xUnit tests with Moq & FluentAssertions |
| DTOs generation | \`withDtos: true\` | Create, Update, Response DTOs |
| Validation | \`withValidation: true\` | FluentValidation validators |
| Repository | \`withRepository: true\` | Interface + Implementation |
| Migration naming | \`suggest_migration\` | {context}_v{version}_{seq}_{Desc} |
| Layout wrapper | No \`max-w-*\` | Content fills available width |
| Layout padding | \`lg:px-10\` | Standard horizontal padding |
| Tab navigation | \`useTabNavigation\` hook | Sync tabs with URL |
| Tab URL | \`?tab=name\` | Shareable deep links to tabs |
| Tab lazy loading | \`enabled: activeTab === 'x'\` | Load data only for active tab |
| Breadcrumb | \`<Breadcrumb items={[...]}/>\` | Navigation hierarchy, first page element |
| Breadcrumb items | \`{ label, href?, icon? }\` | Last item without href = current page |

---

## 9. MCP Tools & Resources

### Available Tools

| Tool | Description |
|------|-------------|
| \`scaffold_extension\` | Generate code: feature (full-stack), entity, service, controller, component, dto, validator, repository, or test |
| \`validate_conventions\` | Validate SmartStack conventions compliance |
| \`check_migrations\` | Analyze EF Core migrations for conflicts |
| \`api_docs\` | Get API documentation from Swagger/Controllers with examples |
| \`suggest_migration\` | Suggest migration name following conventions |

### Available Resources

| URI | Description |
|-----|-------------|
| \`smartstack://conventions\` | This documentation |
| \`smartstack://project\` | Project structure information |
| \`smartstack://entities\` | Quick reference of all domain entities |
| \`smartstack://entities/{filter}\` | Filtered entity list by name/prefix |
| \`smartstack://schema\` | Database schema information |
| \`smartstack://api\` | API endpoints documentation |

### Scaffold Extension

Generate tenant-aware code by default:

**Type Options:**

| Type | Description | Generated Files |
|------|-------------|-----------------|
| \`feature\` | **Full-stack generation** | Entity + DTOs + Validators + Repository + Service + Controller + Component |
| \`entity\` | Domain entity only | Entity class + EF Configuration |
| \`dto\` | DTOs only | Create, Update, Response DTOs |
| \`validator\` | Validators only | FluentValidation validators |
| \`repository\` | Repository only | Interface + Implementation |
| \`service\` | Service layer only | Interface + Implementation |
| \`controller\` | API controller only | REST Controller |
| \`component\` | React component only | Component + Hook + Types |
| \`test\` | Unit tests only | xUnit tests with Moq |

#### Full-Stack Feature (Recommended)

Generate all layers in one command:

\`\`\`json
// Complete feature: Entity → Service → Controller → Component
{
  "type": "feature",
  "name": "Product",
  "options": {
    "tablePrefix": "ref_",
    "dryRun": true,       // Preview first
    "withTests": true     // Include unit tests
  }
}

// Client extension (uses 'extensions' schema + 'ext_' prefix)
{
  "type": "feature",
  "name": "CustomReport",
  "options": {
    "clientExtension": true,  // Auto-sets schema + prefix
    "skipController": true    // Skip API, React-only
  }
}
\`\`\`

#### Individual Types

\`\`\`json
// Entity (default: tenant-aware with ITenantEntity)
{
  "type": "entity",
  "name": "Product",
  "options": {
    "tablePrefix": "ref_",
    "dryRun": true  // Preview without writing files
  }
}

// System entity (no TenantId)
{
  "type": "entity",
  "name": "GlobalConfig",
  "options": {
    "isSystemEntity": true,
    "tablePrefix": "cfg_"
  }
}

// Unit tests for a service
{
  "type": "test",
  "name": "Product"
}
\`\`\`

**Common Options:**

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`dryRun\` | boolean | false | Preview generated code without writing files |
| \`isSystemEntity\` | boolean | false | Create system entity (no TenantId) |
| \`tablePrefix\` | string | "ref_" | Domain prefix for table name |
| \`schema\` | string | "core" | Database schema ("core" or "extensions") |
| \`namespace\` | string | auto | Custom namespace |
| \`baseEntity\` | string | - | Base entity to extend |

**Feature-Specific Options:**

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`clientExtension\` | boolean | false | Use 'extensions' schema + 'ext_' prefix |
| \`skipService\` | boolean | false | Skip service layer generation |
| \`skipController\` | boolean | false | Skip API controller generation |
| \`skipComponent\` | boolean | false | Skip React component generation |
| \`withTests\` | boolean | false | Generate unit tests (xUnit + Moq) |
| \`withDtos\` | boolean | false | Generate DTOs (Create, Update, Response) |
| \`withValidation\` | boolean | false | Generate FluentValidation validators |
| \`withRepository\` | boolean | false | Generate repository pattern |
| \`entityProperties\` | array | - | Define entity properties for DTOs |

### Suggest Migration

Generate migration names following conventions:

\`\`\`json
{
  "description": "Add User Profiles",
  "context": "core",       // optional: core or extensions
  "version": "1.2.0"       // optional: auto-detected from existing
}
\`\`\`

Output: \`core_v1.2.0_001_AddUserProfiles\`

### Validate Conventions

Run specific or all checks:

\`\`\`json
{
  "checks": ["tables", "migrations", "services", "namespaces", "entities", "tenants"]
}
\`\`\`

**Check Types:**

| Check | Validates |
|-------|-----------|
| \`tables\` | Schema specification, domain prefixes |
| \`migrations\` | Naming convention, version ordering |
| \`services\` | Interface implementation pattern |
| \`namespaces\` | Layer namespace structure |
| \`entities\` | BaseEntity/SystemEntity inheritance, factory methods |
| \`tenants\` | ITenantEntity implementation, TenantId consistency |
`;
}